import { AgencyTeam, GlobalTeam, MarketTeam } from "./types"
import { Authority, useAuthenticated } from "providers/A12n"
import { CellProps, Column } from "react-table"
import { css, cx } from "@emotion/css"

import { Avatar } from "@material-ui/core"
import MoreActions from "components/MoreActions"
import { MorePopover } from "components/MorePopover"
import { colorOptions } from "utils/color"

export const columns = (
  t: (k: string) => string,
  onDelete: (id: string) => void,
  hasAuthority: (allowed: Authority[]) => boolean,
  onEdit?: (id: string) => void,
): Array<
  Column<GlobalTeam | MarketTeam | AgencyTeam> & {
    header: string
    fixed?: boolean
    default: boolean
    variant?: string
    sortable?: boolean
  }
> => {
  const market_columns = [
    {
      sortable: false,
      header: "teams.list.agencies",
      id: "agencies",
      Cell: function (props: CellProps<MarketTeam>) {
        const { agencies } = props.row.original
        const agenciesToShow = agencies?.slice(0, 1)?.map((c) => c?.name)
        return (
          <div>
            {agenciesToShow}
            <MorePopover
              data={agencies}
              label="market-agencies.agencies"
              name="name"
            />
          </div>
        )
      },
    },
    {
      sortable: false,
      header: "teams.list.companies",
      id: "companies",
      Cell: function (props: CellProps<MarketTeam>) {
        const { companies } = props.row.original
        const companiesToShow = companies?.slice(0, 1)?.map((c) => c?.name)
        return (
          <div>
            {companiesToShow}
            <MorePopover
              data={companies}
              label="market-agencies.companies"
              name="name"
            />
          </div>
        )
      },
    },
  ]

  const action_columns = [
    {
      align: "right",
      sortable: false,
      header: "teams.list.actions",
      id: "actions",
      Cell: function (props: CellProps<GlobalTeam>) {
        const { id } = props.row.original
        const { hasAuthority } = useAuthenticated()

        return (
          <MoreActions
            options={[
              // EDIT
              {
                hidden: !hasAuthority(["AUTH_EDIT_USER_MARKET"]),
                label: t("product.edit.action"),
                onClick: () => !!onEdit && onEdit(id),
              },
              // DELETE
              {
                hidden: !hasAuthority([
                  "AUTH_EDIT_TEAM",
                  "AUTH_DELETE_USER_MARKET",
                ]),
                label: t("teams.member.list.delete.action"),
                onClick: () => onDelete(id),
              },
            ]}
          />
        )
      },
    },
  ]

  let custom_columns = [] as any
  if (hasAuthority(["AUTH_ACCESS_GLOBAL_ADMIN"])) {
    custom_columns = global_columns
  } else if (hasAuthority(["AUTH_ACCESS_MARKET_ADMIN"])) {
    custom_columns = [...market_columns, ...action_columns]
  } else if (hasAuthority(["AUTH_ACCESS_MARKET_FINANCE"])) {
    custom_columns = [...market_columns]
  } else if (hasAuthority(["AUTH_ACCESS_AGENCY_ADMIN"])) {
    custom_columns = action_columns
  }

  return [
    {
      sortable: false,
      header: "teams.list.name",
      id: "name",
      Cell: function (props: CellProps<GlobalTeam>) {
        const { name, firstname = "", lastname = "" } = props.row.original
        const initials = `${firstname[0]}${lastname[0]}`
        return (
          <div className={styles.name}>
            <Avatar
              className={cx("BodyBody-sWhiteRegular", styles.avatar)}
              style={
                {
                  "--bg-color":
                    colorOptions[
                      Math.floor(Math.random() * colorOptions.length)
                    ],
                } as React.CSSProperties
              }
            >
              {initials}
            </Avatar>
            <span>{name}</span>
          </div>
        )
      },
    },
    {
      sortable: false,
      header: "teams.list.mail",
      id: "mail",
      accessor: "mail",
    },
    {
      sortable: false,
      header: "teams.list.role",
      id: "role",
      Cell: function (props: CellProps<GlobalTeam | MarketTeam>) {
        const { role } = props.row.original
        return <div>{!!role ? t(`role.list.${role}`) : "-"}</div>
      },
    },
    ...custom_columns,
  ]
}

const global_columns = [
  {
    sortable: false,
    header: "teams.list.market",
    id: "market",
    accessor: "marketName",
  },
  {
    sortable: false,
    header: "teams.list.subRegion",
    id: "subRegionName",
    accessor: "subRegionName",
  },
]

const styles = {
  column: css`
    display: flex;
    flex-direction: column;
  `,
  hover: css`
    color: var(--primary-main);
    cursor: pointer;
  `,
  popover: css`
    pointer-events: none;
  `,
  paper: css`
    padding: 1rem;
    display: flex;
    flex-direction: column;
  `,
  name: css`
    display: flex;
    align-items: center;
  `,
  avatar: css`
    width: var(--spacing-xxl-2);
    height: var(--spacing-xxl-2);
    --bg-color: inherit;
    background-color: var(--bg-color);
    text-transform: uppercase;
    margin-right: var(--spacing-xs);
  `,
}
